import { ArrowRightIcon, CircleAlertIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'

import { useEmbeddedPartner } from 'components/hooks/employer_onboarding/useEmbeddedPartner'
import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Banner, Button, CollapsibleContainer, List } from 'slab'
import { useGetQuotingProspectsProspectIdCensusCensusIdEmployees, usePostQuotingProspectsProspectIdCensusCensusIdEmployeesBulkReplaceDependents } from 'utils/web_api'

import { useCensus } from '../CensusProvider'
import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { emitEmployerOnboardingEvent, parseError } from '../utils'
import { DependentsEditor } from './census/DependentsEditor'

type Dependent = { relationship: 'spouse' | 'child' | 'self'; date_of_birth: string }
type Employee = { id: string; first_name?: string; last_name?: string; name?: string | null; dependents: Dependent[] }
type FormShape = { employees: Array<Employee> }

export function EmbeddedCensusDependentsPage () {
  const { data: partner } = useEmbeddedPartner()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { employer } = useEmployer()
  const { census } = useCensus()
  const { setError, errorBanner } = useAPIErrorBanner({ fallbackMessage: 'Failed to save dependents' })

  const prospectId = employer?.quoting_prospect_id ?? ''
  const censusId = census?.id ?? ''

  const { data: employeesResp, isLoading, error } = useGetQuotingProspectsProspectIdCensusCensusIdEmployees(
    prospectId,
    censusId,
    { 'page[size]': 100 },
    { query: { refetchOnMount: 'always', enabled: !!censusId } }
  )

  const employees = employeesResp?.data ?? []

  // Track which collapsible containers are open (default all open)
  const [openStates, setOpenStates] = useState<Record<string, boolean>>(() =>
    employees.reduce<Record<string, boolean>>((acc, emp) => ({ ...acc, [emp.id]: true }), {})
  )

  // Update openStates when employees data changes
  useEffect(() => {
    if (employees.length === 0) return

    setOpenStates(prev => {
      const newState: Record<string, boolean> = {}

      // Default to open for new employees
      employees.forEach(emp => {
        newState[emp.id] = prev[emp.id] ?? true
      })

      return newState
    })
  }, [employees])

  const dependentsMutation = usePostQuotingProspectsProspectIdCensusCensusIdEmployeesBulkReplaceDependents({
    mutation: {
      onSuccess: () => {
        emitEmployerOnboardingEvent('[ER] Dependents updated', {})
        navigateToNextStep()
      },
      onError: setError,
    },
  })

  const methods = useForm<FormShape>({
    defaultValues: {
      employees: employees.map((e) => ({ id: e.id, first_name: e.first_name, last_name: e.last_name, name: e.name, dependents: e.dependents || [] })),
    },
    values: {
      employees: employees.map((e) => ({ id: e.id, first_name: e.first_name, last_name: e.last_name, name: e.name, dependents: e.dependents || [] })),
    },
  })

  const { fields: employeeFields } = useFieldArray({ control: methods.control, name: 'employees' })

  // Check which employees have validation errors
  const employeesWithErrors = useMemo(() => {
    const errors = methods.formState.errors.employees
    if (!errors || !Array.isArray(errors)) return new Set<number>()

    return new Set(
      errors
        .map((err, idx) => err ? idx : null)
        .filter((idx): idx is number => idx !== null)
    )
  }, [methods.formState.errors.employees, methods.formState.submitCount])

  // Auto-expand employees with errors after validation attempt
  useEffect(() => {
    if (methods.formState.isSubmitted && employeesWithErrors.size > 0) {
      setOpenStates(prev => {
        const newState = { ...prev }
        employeesWithErrors.forEach(idx => {
          const employee = employeeFields[idx]
          if (employee) {
            newState[employee.id] = true
          }
        })
        return newState
      })
    }
  }, [methods.formState.isSubmitted, employeesWithErrors, employeeFields])

  // Error summary banner
  const validationErrorBanner = useMemo(() => {
    if (!methods.formState.isSubmitted || employeesWithErrors.size === 0) return null

    const maxNamesToShow = 5
    const errorCount = employeesWithErrors.size

    if (errorCount > maxNamesToShow) {
      return (
        <Banner type='critical' title='Please fix validation errors'>
          {errorCount} employees have incomplete or invalid dependent information. Please review the sections marked with a red warning icon above.
        </Banner>
      )
    }

    const employeeInfo = Array.from(employeesWithErrors)
      .map(idx => {
        const employee = employeeFields[idx]
        return {
          id: employee.id,
          name: employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Employee'
        }
      })

    return (
      <Banner type='critical' title='Please fix validation errors'>
        The following employees have incomplete or invalid dependent information:
        <List>
          {employeeInfo.map(({ id, name }) => (
            <List.Item key={id}>{name}</List.Item>
          ))}
        </List>
      </Banner>
    )
  }, [methods.formState.isSubmitted, employeesWithErrors, employeeFields])

  const onSubmit = useCallback(async () => {
    // Trigger validation first
    const isValid = await methods.trigger()
    if (!isValid) {
      // Validation failed, the useEffect above will expand error sections
      return
    }

    const updatesByEmployeeId = methods.getValues('employees').reduce<Record<string, Dependent[]>>((acc, e) => {
      acc[e.id] = e.dependents
      return acc
    }, {})

    dependentsMutation.mutate({
      prospectId,
      censusId,
      data: { updates_by_employee_id: updatesByEmployeeId },
    })
  }, [methods, dependentsMutation, prospectId, censusId])

  const heading = 'To calculate budgets, we have loaded in your team'
  const partnerName = partner?.friendly_name ?? partner?.display_name

  const subheading = `Thatch has ${partnerName ? `partnered with ${partnerName} to load in your team's data` : 'synced your eligible employees'}. Please review and add dependents where needed.`

  function renderContent () {
    if (!prospectId || !censusId) {
      return (
        <Banner type='warning' title='Cannot edit dependents for this employer'>
          We cannot edit dependents for the current employer. If this problem persists, please contact support.
        </Banner>
      )
    }
    if (isLoading) return <div>Loading...</div>
    if (error) {
      return (
        <Banner type='critical' title='An error occurred loading your account information'>
          {parseError(error, '').message}
        </Banner>
      )
    }

    return (
      <FormProvider {...methods}>
        <div className='flex flex-col gap-6'>
          {employeeFields.map((employee, index) => {
            const employeeName = employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Employee'

            return (
              <div key={employee.id}>
                <CollapsibleContainer
                  heading={employeeName}
                  open={openStates[employee.id] ?? true}
                  onOpenChange={(isOpen) => setOpenStates(prev => ({ ...prev, [employee.id]: isOpen }))}
                  headingIconContent={
                    employeesWithErrors.has(index) && (
                      <div className='text-red-400'>
                        <CircleAlertIcon aria-label='(has errors)' size={16} />
                      </div>
                    )
                  }
                >
                  <div className='flex flex-col gap-4 p-4'>
                    {/* Dependents only - no employee editing, no add/remove employees */}
                    <DependentsEditor employeeIndex={index} />
                  </div>
                </CollapsibleContainer>
              </div>
            )
          })}
        </div>
      </FormProvider>
    )
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header heading={heading} subheading={subheading} />
      <CenteredLayout.Container>
        {renderContent()}
        {validationErrorBanner}
        {errorBanner}
      </CenteredLayout.Container>
      <CenteredLayout.Buttons>
        <Button variant='secondary' onClick={navigateToPreviousStep}>Back</Button>
        <Button
          variant='primary'
          onClick={() => methods.handleSubmit(() => onSubmit())()}
          rightIcon={ArrowRightIcon}
          disabled={dependentsMutation.isPending}
        >
          Next step
        </Button>
      </CenteredLayout.Buttons>
    </CenteredLayout>
  )
}
